import type React from "react";
import { createContext, useContext } from "react";
import type {
  HoverIntent,
  PositionAnchor,
  PositionSide,
} from "../../primitives/index.js";

/* The context the Popover parts share. Its own file so PopoverMenuItem can
   live in PopoverMenuItem.tsx without an import cycle through Popover.tsx.
   Internal: not exported from the barrel. */

export type Align = "start" | "end" | "center";
export type ContentRole = "menu" | "dialog" | "listbox";

/** How a keyboard open wants focus seeded. Consumed and cleared once by the content. */
export type FocusOnOpen = "seed" | "last";

export interface PopoverCtx {
  open: boolean;
  setOpen: (v: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  /** What the content is placed against: the Popover's `anchorRef` when it
   *  has one, otherwise `triggerRef`. */
  anchorRef: React.RefObject<PositionAnchor | null>;
  contentId: string;
  align: Align;
  placement: PositionSide;
  contentRole: ContentRole;
  layerId: string;
  /**
   * Set by the trigger on a KEYBOARD open, consumed once by the content.
   *
   * A mouse click must never pull focus into the menu — that is the behaviour every
   * existing consumer has, and a hover-opened menu depends on focus staying put.
   */
  focusOnOpenRef: React.MutableRefObject<FocusOnOpen | null>;
  /** Signals that a selected item closed the menu, so focus should return. */
  restoreFocusOnCloseRef: React.MutableRefObject<boolean>;
  /** openOn="hover" timers and pin. Every call does nothing on a click Popover. */
  hover: HoverIntent;
}

export const PopoverContext = createContext<PopoverCtx | null>(null);

export function usePopoverContext(): PopoverCtx {
  const c = useContext(PopoverContext);
  if (!c) throw new Error("Popover.* must be used within <Popover>");
  return c;
}
